import { useState, useMemo } from "react";
import {
	ChevronDown,
	ChevronRight,
	CheckCircle2,
	Loader2,
	AlertTriangle,
	XCircle,
	ArrowRight,
	Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTechnicalAuditIssues } from "@/hooks/useTechnicalAuditIssues";
import type { AuditRunSummary } from "@/types/technicalAudit";
import { computeHealthScore as computeHealthScoreFromCounts } from "./healthScore";

function formatRelative(iso: string | null): string {
	if (!iso) return "Never audited";
	const then = new Date(iso).getTime();
	const diffMs = Date.now() - then;
	if (diffMs < 0) return "just now";
	const sec = Math.floor(diffMs / 1000);
	if (sec < 60) return "just now";
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m ago`;
	const hr = Math.floor(min / 60);
	if (hr < 24) return `${hr}h ago`;
	const day = Math.floor(hr / 24);
	if (day < 30) return `${day}d ago`;
	const mo = Math.floor(day / 30);
	if (mo < 12) return `${mo}mo ago`;
	return `${Math.floor(mo / 12)}y ago`;
}

// Delegates to the shared, size-normalized health score (GH #596) so the overview
// card and the issues page agree and no site floors at 0/100.
function computeHealthScore(run: AuditRunSummary): number {
	return computeHealthScoreFromCounts(
		run.issues_by_severity,
		run.pages_crawled,
	);
}

function healthColor(score: number): string {
	if (score < 50) return "var(--color-coral-dark)";
	if (score < 76) return "var(--color-coral)";
	return "var(--color-teal)";
}

function HealthRing({ score, size = 68 }: { score: number; size?: number }) {
	const stroke = 7;
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const offset = c - (score / 100) * c;
	const color = healthColor(score);
	return (
		<svg width={size} height={size} className="-rotate-90">
			<circle
				cx={size / 2}
				cy={size / 2}
				r={r}
				fill="none"
				stroke="#F1F1F1"
				strokeWidth={stroke}
			/>
			<circle
				cx={size / 2}
				cy={size / 2}
				r={r}
				fill="none"
				stroke={color}
				strokeWidth={stroke}
				strokeDasharray={c}
				strokeDashoffset={offset}
				strokeLinecap="round"
				style={{ transition: "stroke-dashoffset 0.4s ease" }}
			/>
			<text
				x="50%"
				y="50%"
				textAnchor="middle"
				dominantBaseline="central"
				className="font-bold fill-gray-900"
				fontSize={size * 0.32}
				transform={`rotate(90 ${size / 2} ${size / 2})`}
			>
				{score}
			</text>
		</svg>
	);
}

function Sparkline({ scores }: { scores: number[] }) {
	if (scores.length < 2) return null;
	const w = 80;
	const h = 24;
	const min = Math.min(...scores);
	const max = Math.max(...scores);
	const range = max - min || 1;
	const points = scores
		.map((s, i) => {
			const x = (i / (scores.length - 1)) * w;
			const y = h - ((s - min) / range) * h;
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");
	const last = scores[scores.length - 1];
	return (
		<svg width={w} height={h} className="overflow-visible">
			<polyline
				points={points}
				fill="none"
				stroke={healthColor(last)}
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

interface AuditSiteCardProps {
	website: string;
	runs: AuditRunSummary[];
	onSelectRun: (runId: string) => void;
}

const ISSUE_TYPE_LABELS: Record<string, string> = {
	title_meta: "Missing or Bad Title/Meta",
	h1: "Missing or Bad H1",
	h1_missing: "Missing H1",
	h1_multiple: "Multiple H1 Tags",
	h1_too_long: "H1 Too Long",
	alt_text: "Missing Image Alt Text",
	redirect: "Redirect Chains",
	broken_link: "Broken Internal Links",
	broken_internal: "Broken Internal URLs",
	canonical: "Canonical Issues",
	duplicate_title: "Duplicate SEO Title Tags",
	page_title_same_as_h1: "Title Matches H1",
	page_title_missing: "Missing SEO Titles",
	page_title_over_60_chars: "SEO Titles Too Long",
	page_title_below_30_chars: "SEO Titles Too Short",
	duplicate_meta: "Duplicate Meta Descriptions",
	meta_missing: "Missing Meta Descriptions",
	meta_description_over_155_chars: "Meta Descriptions Too Long",
	meta_description_below_70_chars: "Meta Descriptions Too Short",
	thin_content: "Thin Content",
	indexability: "Indexability Issues",
	slow_response: "Slow Response Time",
	near_duplicate: "Near-Duplicate Content",
	orphan_page: "Orphan Pages",
	schema_error: "Schema Errors",
	missing_schema: "Missing Schema",
	schema_validation_error: "Schema Validation Errors",
	missing_og_tags: "Missing Open Graph Tags",
	mixed_content: "Mixed Content",
	redirect_loop: "Redirect Loops",
	title_too_short: "Title Too Short",
	serp_title_truncated: "SERP Title Truncated",
	serp_meta_truncated: "SERP Description Truncated",
	multiple_meta: "Multiple Meta Descriptions",
	duplicate_h1: "Duplicate H1",
	low_readability: "Low Readability",
	low_text_ratio: "Low Text-to-HTML Ratio",
	stale_content: "Stale Content",
	broken_external: "Broken External Links",
	deep_page: "Deep Pages",
	large_image: "Oversized Images",
	broken_image: "Broken Images",
	non_nextgen_image: "Non-Next-Gen Images",
	large_page: "Oversized Pages",
	unsafe_crossorigin: "Unsafe Cross-Origin Links",
	nofollow_internal: "Nofollow Internal Links",
	low_internal_links: "Low Internal Links",
	missing_hsts: "Missing HSTS",
	hreflang_error: "Hreflang Errors",
	sitemap_error: "Sitemap Issues",
	missing_from_sitemap: "Missing from Sitemap",
	performance_diagnostics: "Performance Diagnostics",
	keyword_cannibalization: "Keyword Cannibalization",
	image_missing_dimensions: "Images Missing Dimensions",
	core_web_vitals: "Core Web Vitals",
};

const labelForType = (type: string): string =>
	ISSUE_TYPE_LABELS[type] ||
	type
		.split("_")
		.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
		.join(" ");

const statusIcon = (status: AuditRunSummary["status"]) => {
	switch (status) {
		case "completed":
			return <CheckCircle2 className="w-5 h-5 text-teal" />;
		case "running":
		case "pending":
			return <Loader2 className="w-5 h-5 text-coral animate-spin" />;
		case "failed":
			return <XCircle className="w-5 h-5 text-destructive" />;
		default:
			return <AlertTriangle className="w-5 h-5 text-muted-foreground" />;
	}
};

const statusLabel = (status: AuditRunSummary["status"]) => {
	if (status === "running" || status === "pending") return "In progress";
	return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatDate = (iso: string | null) =>
	iso
		? new Date(iso).toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
		  })
		: "—";

const severityCount = (run: AuditRunSummary, key: string) =>
	run.issues_by_severity?.[key] ?? 0;

export function AuditSiteCard({
	website,
	runs,
	onSelectRun,
}: AuditSiteCardProps) {
	const [historyOpen, setHistoryOpen] = useState(false);

	const sorted = useMemo(
		() =>
			[...runs].sort(
				(a, b) =>
					new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
			),
		[runs],
	);
	const latest = sorted[0];
	const latestCompleted = sorted.find((r) => r.status === "completed");
	const history = sorted.filter((r) => r.id !== latest?.id);
	const trendScores = useMemo(() => {
		const completed = sorted.filter((r) => r.status === "completed");
		return completed
			.slice(0, 8)
			.reverse()
			.map((r) => computeHealthScore(r));
	}, [sorted]);

	// Fetch issue type breakdown for the latest completed run to surface top fixes.
	// pageSize: 1 keeps the row payload small; we only consume `summary.by_type`.
	const { data: issuesData } = useTechnicalAuditIssues(
		latestCompleted?.id || "",
		undefined,
		{ pageSize: 1 },
	);

	const topFixes = Object.entries(issuesData?.summary?.by_type || {})
		.filter(([, count]) => count > 0)
		.sort(([, a], [, b]) => b - a)
		.slice(0, 3);

	if (!latest) return null;

	const critical = severityCount(latest, "critical");
	const warning = severityCount(latest, "warning");
	const info = severityCount(latest, "info");
	const total = latest.total_issues ?? 0;
	const healthScore = computeHealthScore(latest);
	const lastRunIso = latest.completed_at || latest.started_at;

	return (
		<div className="rounded-xl border bg-card shadow-sm overflow-hidden mb-4">
			<div className="px-6 py-5">
				<div className="flex items-start justify-between gap-6">
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2 mb-1">
							{statusIcon(latest.status)}
							<h3 className="text-lg font-semibold text-foreground truncate">
								{website}
							</h3>
							<span className="text-xs text-muted-foreground font-medium">
								{statusLabel(latest.status)}
							</span>
						</div>
						<div className="flex items-center gap-3 text-sm text-muted-foreground">
							<span className="inline-flex items-center gap-1">
								<Clock className="w-3.5 h-3.5" />
								{formatRelative(lastRunIso)}
							</span>
							{trendScores.length >= 2 && (
								<span className="inline-flex items-center gap-1">
									<span className="text-xs">trend</span>
									<Sparkline scores={trendScores} />
								</span>
							)}
						</div>

						<div className="flex items-center gap-2 mt-4 text-sm">
							<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-destructive/5 text-destructive font-medium">
								<span className="w-1.5 h-1.5 rounded-full bg-destructive" />
								{critical} critical
							</span>
							<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-coral-light text-coral-dark font-medium">
								<span className="w-1.5 h-1.5 rounded-full bg-coral" />
								{warning} warning
							</span>
							<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-light text-teal-dark font-medium">
								<span className="w-1.5 h-1.5 rounded-full bg-teal" />
								{info} info
							</span>
							<span className="text-xs text-muted-foreground">
								· {total} total
							</span>
						</div>
					</div>

					<div className="shrink-0 flex flex-col items-center gap-1">
						<HealthRing score={healthScore} />
						<div className="text-[10px] text-muted-foreground uppercase tracking-wide">
							Health
						</div>
					</div>
				</div>

				{topFixes.length > 0 && latestCompleted && (
					<div className="mt-5 pt-4 border-t">
						<div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
							Top Fix Actions
						</div>
						<ul className="divide-y">
							{topFixes.map(([type, count]) => (
								<li
									key={type}
									className="flex items-center justify-between py-2.5 text-sm"
								>
									<div className="flex items-center gap-2.5 min-w-0">
										<AlertTriangle className="w-4 h-4 text-coral shrink-0" />
										<span className="text-foreground font-medium truncate">
											{labelForType(type)}
										</span>
										<span className="text-muted-foreground shrink-0">
											{count} {count === 1 ? "page" : "pages"}
										</span>
									</div>
									<button
										type="button"
										onClick={() => onSelectRun(latestCompleted.id)}
										className="text-coral hover:text-coral-hover font-medium shrink-0"
									>
										Fix →
									</button>
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="flex items-center justify-between mt-5 pt-4 border-t">
					<Button
						onClick={() => latestCompleted && onSelectRun(latestCompleted.id)}
						disabled={!latestCompleted}
						className="bg-coral hover:bg-coral-hover text-white"
					>
						View Latest Issues
						<ArrowRight className="w-4 h-4 ml-1.5" />
					</Button>

					{history.length > 0 && (
						<button
							type="button"
							onClick={() => setHistoryOpen((v) => !v)}
							className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							{historyOpen ? (
								<ChevronDown className="w-4 h-4" />
							) : (
								<ChevronRight className="w-4 h-4" />
							)}
							{history.length} prior {history.length === 1 ? "audit" : "audits"}
						</button>
					)}
				</div>
			</div>

			{historyOpen && history.length > 0 && (
				<div className="border-t bg-warm-bg px-6 py-4">
					<div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
						Audit History
					</div>
					<ul className="divide-y">
						{history.map((run) => (
							<li
								key={run.id}
								className="flex items-center justify-between py-2.5 text-sm"
							>
								<div className="flex items-center gap-3 min-w-0">
									{statusIcon(run.status)}
									<span className="text-foreground">
										{formatDate(run.completed_at || run.started_at)}
									</span>
									<span className="text-muted-foreground">·</span>
									<span className="text-muted-foreground truncate">
										{run.total_issues ?? 0} issues
										{severityCount(run, "critical") > 0 && (
											<>
												{" "}
												<span className="text-destructive font-medium">
													({severityCount(run, "critical")} critical)
												</span>
											</>
										)}
									</span>
								</div>
								<button
									type="button"
									onClick={() => onSelectRun(run.id)}
									disabled={run.status !== "completed"}
									className="text-coral hover:text-coral-hover font-medium disabled:opacity-40 disabled:cursor-not-allowed"
								>
									View →
								</button>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
